// @ts-nocheck
import { forwardRef, useEffect, useState } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { FadeIn } from '../../../animations';
import type { KpiCardProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * KpiCardMinimal - Minimalist KPI card
 *
 * Ultra-compact KPI display without borders, backgrounds, or icons.
 * Perfect for dense dashboards or when you need many metrics in a small space.
 */
const KpiCardMinimal = forwardRef<HTMLDivElement, Omit<KpiCardProps, 'icon'>>(
  (
    {
      title,
      value,
      description,
      color = 'blue',
      size = 'md',
      onClick,
      animate = false,
      animationDuration = 2000,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [displayValue, setDisplayValue] = useState(animate ? 0 : value);

    const sizes = {
      xs: {
        titleSize: '10px',
        valueSize: 'md',
        gap: 4,
      },
      sm: {
        titleSize: 'xs',
        valueSize: 'lg',
        gap: 6,
      },
      md: {
        titleSize: 'sm',
        valueSize: 'xl',
        gap: 8,
      },
      lg: {
        titleSize: 'md',
        valueSize: '2rem',
        gap: 10,
      },
      xl: {
        titleSize: 'lg',
        valueSize: '2.5rem',
        gap: 12,
      },
    };

    const currentSize = sizes[size];

    // Animated counter
    useEffect(() => {
      if (!animate || typeof value !== 'number') {
        setDisplayValue(value);
        return;
      }

      const startValue = 0;
      const endValue = value;
      const duration = animationDuration;
      const startTime = Date.now();

      const animateValue = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(startValue + (endValue - startValue) * easeOut);

        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animateValue);
        } else {
          setDisplayValue(endValue);
        }
      };

      requestAnimationFrame(animateValue);
    }, [value, animate, animationDuration]);

    const isDark = colorScheme === 'dark';

    return (
      <FadeIn>
        <Box
          ref={ref}
          onClick={onClick}
          className={className}
          style={{
            cursor: onClick ? 'pointer' : 'default',
            padding: '8px 0',
          }}
        >
          <Stack gap={currentSize.gap}>
            <Text
              size={currentSize.titleSize}
              c="dimmed"
              tt="uppercase"
              fw={600}
              style={{ letterSpacing: '0.5px', ...accessibilityStyles }}
            >
              {title}
            </Text>

            <Text
              size={currentSize.valueSize}
              fw={700}
              c={theme.colors[color][isDark ? 4 : 6]}
              style={{
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {displayValue}
            </Text>

            {description && (
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                {description}
              </Text>
            )}
          </Stack>
        </Box>
      </FadeIn>
    );
  }
);

KpiCardMinimal.displayName = 'KpiCardMinimal';

export default KpiCardMinimal;
